import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Checkbox from '@mui/material/Checkbox';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ListItemText from '@mui/material/ListItemText';
import './FilterSidebar.scss';
import { useFilters } from '../../context/FilterContext.jsx';
import { fetchClientsByPrefix, fetchAccountsByPrefix } from '../../services/api/growthHacksService.js';

export default function FilterSidebar({ isOpen = true }) {
  const { 
    filters, 
    updateFilters, 
    resetFilters 
  } = useFilters();
  
  const [clientSearchInput, setClientSearchInput] = useState('');
  const [searchedClients, setSearchedClients] = useState([]);
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const [selectedClients, setSelectedClients] = useState([]);
  const [accountSearchInput, setAccountSearchInput] = useState('');
  const [searchedAccounts, setSearchedAccounts] = useState([]);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false);
  const [isClientsDropdownOpen, setIsClientsDropdownOpen] = useState(false);
  
  const { 
    fiscalYear, 
    subSector, 
    account, 
    clientName,
    clientId, 
    ghType, 
    category: ghCategory, 
    ghPriority 
  } = filters;
  
  const handleChange = (field, value) => {
    console.log(`Filter changed: ${field} = ${value}`);
    updateFilters({ [field]: value });
  };

  useEffect(() => {
    if (clientName === 'All' || clientName === '' || !clientName) {
      setSelectedClients([]);
      setClientSearchInput('');
    } else if (clientId && clientName) {
      const clientNames = clientName.split(',').map(name => name.trim());
      const clientIds = clientId.split(',').map(id => id.trim());
      
      const clients = clientNames.map((name, index) => ({
        label: name,
        value: clientIds[index] || ''
      }));
      
      setSelectedClients(clients);
    }
  }, [clientName, clientId]);

  const handleClientSelectionChange = (selectedClientsArray) => {
    setSelectedClients(selectedClientsArray);
    
    if (selectedClientsArray.length === 0) {
      // No clients selected
      updateFilters({ 
        clientName: '',
        clientId: ''
      });
    } else {
      const clientNames = selectedClientsArray.map(client => client.label).join(',');
      const clientIds = selectedClientsArray.map(client => client.value).join(',');
      
      console.log('Multiple clients selected:', clientNames, 'with IDs:', clientIds);
      
      updateFilters({ 
        clientName: clientNames,
        clientId: clientIds
      });
    }
  };

  const handleClientSearch = async (searchValue) => {
    setClientSearchInput(searchValue);
    
    if (searchValue.length >= 3) {
      setIsLoadingClients(true);
      try {
        const response = await fetchClientsByPrefix(searchValue, "", "", "Yes");
        console.log('API response:', response); // Debug log
        
        if (response.success && response.data && Array.isArray(response.data)) {
          // Extract client names from the response - API returns {ClientID, ClientName}
          const clientOptions = response.data.map(client => {
            console.log('Processing client:', client); // Debug log
            
            const clientName = client.ClientName || client.client_name || client.name || String(client);
            const clientId = client.ClientID || client.client_id || client.id || client;
            
            return {
              label: String(clientName), // Ensure it's a string
              value: String(clientId)
            };
          });
          
          console.log('Client options:', clientOptions); // Debug log
          setSearchedClients(clientOptions);
        } else {
          console.log('No valid data in response'); // Debug log
          setSearchedClients([]);
        }
      } catch (error) {
        console.error('Error searching clients:', error);
        setSearchedClients([]);
      } finally {
        setIsLoadingClients(false);
      }
    } else {
      setSearchedClients([]);
    }
  };

  // Handle account search
  const handleAccountSearch = async (searchValue) => {
    setAccountSearchInput(searchValue);
    
    if (searchValue.length >= 3) {
      setIsLoadingAccounts(true);
      try {
        const response = await fetchAccountsByPrefix(searchValue);
        console.log('Account API response:', response); // Debug log
        
        if (response.success && response.data && Array.isArray(response.data)) {
          // Extract account names from the response - API returns {AccountID, AccountName}
          const accountOptions = response.data.map(account => {
            console.log('Processing account:', account); // Debug log
            
            // Handle different possible response formats
            const accountName = account.AccountName || account.account_name || account.name || String(account);
            const accountId = account.AccountID || account.account_id || account.id || account;
            
            return {
              label: String(accountName), // Ensure it's a string
              value: String(accountId)
            };
          });
          
          console.log('Account options:', accountOptions); // Debug log
          setSearchedAccounts(accountOptions);
        } else {
          console.log('No valid data in account response'); // Debug log
          setSearchedAccounts([]);
        }
      } catch (error) {
        console.error('Error searching accounts:', error);
        setSearchedAccounts([]);
      } finally {
        setIsLoadingAccounts(false);
      }
    } else {
      setSearchedAccounts([]);
    }
  };

  // GH Sub-Category options (multi-select)
  const ghSubCategoryOptions = [
    'Client data/insight analytics',
    'Peer group analytics',
    'Trigger / event based'
  ];

  // Parse semicolon-separated string from filters into an array for the multi-select component
  // Filter out legacy 'All' so it doesn't display
  const ghTypeValues = ghType
    ? ghType.split(';').map(s => s.trim()).filter(v => v && v !== 'All')
    : [];

  // Normalize legacy 'All' value in filters to empty string so UI stays clean
  useEffect(() => {
    if (ghType === 'All') {
      updateFilters({ ghType: '' });
    }
  }, [ghType, updateFilters]);

  const handleGhTypeMultiChange = (event) => {
    const { value } = event.target; // MUI provides array (or string in autofill edge cases)
    const rawArray = typeof value === 'string' ? value.split(';').map(s => s.trim()).filter(Boolean) : value;
    const uniqueArray = [...new Set(rawArray)];
    const joined = uniqueArray.length ? uniqueArray.join(';') : '';
    updateFilters({ ghType: joined }); // Empty array => '' per requirement
  };

  // GH Type / Category multi-select options (remove 'All')
  const ghTypeOptions = [
    'Annual/Quarterly reports (Strategy, Risks)',
    'Deals',
    'Financial (Segment) performance',
    'Intelligence',
    'Key Developments',
    'Recent Wins'
  ];


  // Parse existing category (ghCategory) into array, ignore legacy 'All'
  const ghCategoryValues = ghCategory && ghCategory !== 'All'
    ? ghCategory.split(';').map(s => s.trim()).filter(Boolean)
    : [];

  // Default GH Type set (all except 'Recent Wins') for placeholder logic
  const defaultGhTypeValues = ghTypeOptions.filter(o => o !== 'Recent Wins');

  const handleGhCategoryMultiChange = (event) => {
    const { value } = event.target;
    const rawArray = typeof value === 'string' ? value.split(';').map(s => s.trim()).filter(Boolean) : value;
    const uniqueArray = [...new Set(rawArray)];
    const joined = uniqueArray.length ? uniqueArray.join(';') : '';
    updateFilters({ category: joined });
  };

  // Helper to render chips (shared for GH Sub-Category & GH Type)
  const renderChips = (selected) => {
    if (!selected || selected.length === 0) return <span style={{ opacity: 0.6 }}>None</span>;
    const limit = 3;
    const display = selected.slice(0, limit);
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
        {display.map(item => (
          <span
            key={item}
            style={{
              backgroundColor: '#2f373dff',
              padding: '2px 8px',
              margin: '2px',
              borderRadius: '16px',
              fontSize: '12px',
              display: 'inline-block'
            }}
          >{item}</span>
        ))}
        {selected.length > limit && (
          <span
            style={{
              backgroundColor: '#444',
              padding: '2px 8px',
              margin: '2px',
              borderRadius: '16px',
              fontSize: '12px',
              display: 'inline-block'
            }}
          >+{selected.length - limit}</span>
        )}
      </Box>
    );
  };

  const showAccountNoResults = accountSearchInput.length >= 3 && !isLoadingAccounts && searchedAccounts.length === 0;
  const showClientNoResults = clientSearchInput.length >= 3 && !isLoadingClients && searchedClients.length === 0;

  return (
    <Box className={`filter-sidebar ${isOpen ? 'open' : 'closed'}`}>
      <Box className="filters-header">
        <Typography variant="h6">Filters</Typography>
        <Button 
          size="small" 
          variant="outlined" 
          className="reset-btn" 
          onClick={resetFilters}
        >
          Reset
        </Button>
      </Box>
      {/* <Divider style={{ marginBottom: 16, borderColor: '#333' }} /> */}

      <FormControl fullWidth size="small" margin="normal">
        <InputLabel>Fiscal Year</InputLabel>
        <Select 
          value={fiscalYear} 
          label="Fiscal Year" 
          onChange={e => handleChange('fiscalYear', e.target.value)}
        >
          <MenuItem value="FY 26">FY 26</MenuItem>
          <MenuItem value="FY 25">FY 25</MenuItem>
          <MenuItem value="FY 24">FY 24</MenuItem>
        </Select>
      </FormControl>

      <Divider style={{ marginBottom: 16, borderColor: '#333', paddingTop: '10px' }} />

      <FormControl fullWidth size="small" margin="normal">
        <InputLabel>Sub Sector</InputLabel>
        <Select 
          value={subSector} 
          label="Sub Sector" 
          onChange={e => handleChange('subSector', e.target.value)}
        >
          <MenuItem value="All">All</MenuItem>
          <MenuItem value="Aerospace & Defense">Aerospace & Defense</MenuItem>
          <MenuItem value="Automotive">Automotive</MenuItem>
          <MenuItem value="tbd-MA&D">tbd-MA&D</MenuItem>
          <MenuItem value="Transportation">Transportation</MenuItem>
        </Select>
      </FormControl>
      <Divider style={{ marginBottom: 16, borderColor: '#333', paddingTop: '10px' }} />

      <FormControl fullWidth size="small" margin="normal">
        <Autocomplete
          freeSolo
          options={searchedAccounts}
          getOptionLabel={(option) => {
            // Ensure we always return a string
            if (typeof option === 'string') return option;
            if (option && typeof option === 'object' && option.label) return String(option.label);
            return '';
          }}
          value={account === 'All' ? '' : account || ''}
          inputValue={accountSearchInput}
          onInputChange={(event, newInputValue) => {
            handleAccountSearch(newInputValue);
          }}
          onChange={(event, newValue) => {
            let selectedAccountName = '';
            let selectedAccountId = '';
            
            if (typeof newValue === 'string') {
              selectedAccountName = newValue;

              selectedAccountId = '';
            } else if (newValue && typeof newValue === 'object' && newValue.label && newValue.value) {
              selectedAccountName = String(newValue.label);
              selectedAccountId = String(newValue.value);
            }
            
            console.log('Account selected:', selectedAccountName, 'with ID:', selectedAccountId);
            
            updateFilters({ 
              account: selectedAccountName,
              accountId: selectedAccountId
            });
          }}
          loading={isLoadingAccounts}
          sx={{
            '& .MuiAutocomplete-clearIndicator': {
              color: '#ffffff',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              }
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Account Name"
              placeholder="Type at least 3 characters to search..."
              helperText={
                accountSearchInput.length > 0 && accountSearchInput.length < 3
                  ? 'Enter at least 3 characters to search'
                  : showAccountNoResults
                    ? 'No accounts found'
                    : ''
              }
              FormHelperTextProps={{ className: 'filter-helper-text' }}
              size="small"
            />
          )}
          noOptionsText={
            accountSearchInput.length < 3 
              ? "Type at least 3 characters to search" 
              : "No accounts found"
          }
        />
      </FormControl>
      <Divider style={{ marginBottom: 16, borderColor: '#333', paddingTop: '10px' }} />

      <FormControl fullWidth size="small" margin="normal">
        <Autocomplete
          multiple
          options={searchedClients}
          value={selectedClients}
          open={clientSearchInput.length >= 3 && isClientsDropdownOpen}
          onOpen={() => setIsClientsDropdownOpen(true)}
          onClose={() => setIsClientsDropdownOpen(false)}
          getOptionLabel={(option) => {
            // Ensure we always return a string
            if (typeof option === 'string') return option;
            if (option && typeof option === 'object' && option.label) return String(option.label);
            return '';
          }}
          inputValue={clientSearchInput}
          onInputChange={(event, newInputValue) => {
            handleClientSearch(newInputValue);
          }}
          onChange={(event, newValue) => {
            handleClientSelectionChange(newValue);
          }}
          isOptionEqualToValue={(option, value) => {
            return option.value === value.value;
          }}
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Checkbox
                checked={selected}
                style={{ marginRight: 8 }}
              />
              {option.label}
            </li>
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <span
                key={option.value}
                style={{
                  backgroundColor: '#2f373dff',
                  padding: '2px 8px',
                  margin: '2px',
                  borderRadius: '16px',
                  fontSize: '12px',
                  display: 'inline-block'
                }}
                {...getTagProps({ index })}
              >
                {option.label}
              </span>
            ))
          }
          loading={isLoadingClients}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Client name"
              placeholder="Type at least 3 characters to search..."
              helperText={
                selectedClients.length > 0 
                  ? `${selectedClients.length} client${selectedClients.length > 1 ? 's' : ''} selected`
                  : clientSearchInput.length > 0 && clientSearchInput.length < 3 
                    ? 'Enter at least 3 characters to search'
                    : showClientNoResults
                      ? 'No clients found'
                      : ''
              }
              FormHelperTextProps={{ className: 'filter-helper-text' }}
              size="small"
            />
          )}
          noOptionsText={
            clientSearchInput.length < 3 
              ? "Type at least 3 characters to search" 
              : "No clients found"
          }
          disableCloseOnSelect
          limitTags={2}
        />
      </FormControl>
      <Divider style={{ marginBottom: 16, borderColor: '#333', paddingTop: '10px' }} />

      <FormControl fullWidth size="small" margin="normal">
        <InputLabel>GH Sub-Category</InputLabel>
        <Select
          multiple
          size="small"
          value={ghTypeValues}
          label="GH Sub-Category"
          onChange={handleGhTypeMultiChange}
          renderValue={(selected) => renderChips(selected)}
          MenuProps={{
            MenuListProps: { dense: true },
            PaperProps: {
              sx: {
                maxHeight: 220,
                minWidth: 'auto',
                width: 'auto',
                '& .MuiMenuItem-root': {
                  py: 0.5,
                  px: 1.75,
                  minHeight: 42
                },
                '& .MuiCheckbox-root': {
                  p: 0.5
                }
              }
            }
          }}
        >
          {ghSubCategoryOptions.map(option => (
            <MenuItem key={option} value={option} dense>
              <Checkbox
                size="small"
                checked={ghTypeValues.indexOf(option) > -1}
                sx={{
                  color: '#ffffff',
                  p: 0.25,
                  '&.Mui-checked': {
                    color: '#ffffff'
                  }
                }}
              />
              <ListItemText primaryTypographyProps={{ variant: 'body2' }} primary={option} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Divider style={{ marginBottom: 16, borderColor: '#333', paddingTop: '10px' }} />

      <FormControl fullWidth size="small" margin="normal">
        <InputLabel>GH Type</InputLabel>
        <Select
          multiple
          size="small"
          value={ghCategoryValues}
          label="GH Type"
          onChange={handleGhCategoryMultiChange}
          renderValue={(selected) => {
            const isDefault = selected && selected.length === defaultGhTypeValues.length && defaultGhTypeValues.every(v => selected.includes(v));
            if (isDefault) {
              return <span style={{ color: '#ffffff' }}>GH Type</span>;
            }
            return renderChips(selected);
          }}
          MenuProps={{
            MenuListProps: { dense: true },
            PaperProps: {
              sx: {
                maxHeight: 290,
                minWidth: 'auto',
                width: 'auto',
                '& .MuiMenuItem-root': {
                  py: 0.5,
                  px: 1.75,
                  minHeight: 42
                },
                '& .MuiCheckbox-root': {
                  p: 0.5
                }
              }
            }
          }}
        >
          {ghTypeOptions.map(option => (
            <MenuItem key={option} value={option} dense>
              <Checkbox
                size="medium"
                checked={ghCategoryValues.indexOf(option) > -1}
                sx={{
                  color: '#ffffff',
                  p: 0.25,
                  '&.Mui-checked': { color: '#ffffff' }
                }}
              />
              <ListItemText primaryTypographyProps={{ variant: 'body2' }} primary={option} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Divider style={{ marginBottom: 16, borderColor: '#333', paddingTop: '10px' }} />

      <Box marginY={2}>
        <Typography variant="body2" sx={{ color: '#ffffff', mb: 1 }}>GH Priority</Typography>
        <ToggleButtonGroup
          value={ghPriority}
          exclusive
          onChange={(_, val) => handleChange('ghPriority', val || '')}
          aria-label="gh priority"
          size="small"
          color="primary"
          className="gh-type-pill-group"
        >
          <ToggleButton value="Low" className="gh-type-pill">Low</ToggleButton>
          <ToggleButton value="Medium" className="gh-type-pill">Medium</ToggleButton>
          <ToggleButton value="High" className="gh-type-pill">High</ToggleButton>
        </ToggleButtonGroup>
      </Box>
    </Box>
  );
}

